<template>
  <nav
    class="main-menu"
    :class="{ 'main-menu-mobile': mobile }"
  >
    <div
      id="menu"
      v-show="menuIsVisivel"
    >
      <div class="list-group">
        <!-- LINKS -->
        <div
          v-for="item in menuGerado"
          :key="item.label"
          class="opcoes"
        >
          <RouterLink
            class="menu-btn"
            :class="{
              'opcao-selecionada': opcaoSelecionada === item.label,
            }"
            :to="`/${item.route}`"
            @click="handleMenuClick"
          >
            {{ item.label }}
          </RouterLink>
        </div>
        <div
          class="search-wrapper"
          ref="searchRef"
        >
          <input
            ref="searchInputRef"
            v-model="termoBusca"
            type="text"
            :disabled="!mobile && !buscaAberta"
            :class="[
              'search-input',
              {
                aberta: buscaAberta || mobile,
              },
            ]"
            placeholder="Buscar..."
            @keyup.enter="buscar"
          />

          <img
            src="@/assets/lupa.png"
            alt="Buscar"
            class="lupa"
            :class="{ ativa: buscaAberta || mobile }"
            @click="handleClickLupa"
          />
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
import {
  ref,
  watch,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";

import { useRouter, useRoute, RouterLink } from "vue-router";

import { useAuthStore } from "@/stores/auth";
import { storeToRefs } from "pinia";

const props = defineProps({
  mobile: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["fechar-menu"]);

const route = useRoute();
const router = useRouter();

const authStore = useAuthStore();

const { userScope, estaRenovando } = storeToRefs(authStore);

const menuIsVisivel = ref(true);
const opcaoSelecionada = ref("");

const buscaAberta = ref(false);
const termoBusca = ref("");

const searchRef = ref(null);
const searchInputRef = ref(null);

/* =========================
   MENU
========================= */

const menuGerado = computed(() => {
  const escopoAtual = estaRenovando.value
    ? authStore.user?.scope
    : userScope.value;

  const isLogado = escopoAtual && escopoAtual !== "usuarioNaoLogado";

  const menuBase = [
    {
      label: "Atividades",
      page: "atividades",
      route: "Atividades",
    },
    {
      label: "Notícias",
      page: "noticias",
      route: "Noticias",
    },
    {
      label: "Vagas",
      page: "vagas",
      route: "vagas",
    },
    {
      label: "Parcerias",
      page: "parcerias",
      route: "Parcerias",
    },
  ];

  if (isLogado) {
    menuBase.unshift({
      label: "Ações",
      page: "acoes",
      route: "suas-acoes",
    });
  }

  return menuBase;
});

/* =========================
   CLIQUE NOS LINKS
========================= */

function handleMenuClick() {
  if (props.mobile) {
    emit("fechar-menu");
  }
}

/* =========================
   BUSCA
========================= */

async function handleClickLupa() {
  // No mobile o input já está aberto.
  // A lupa serve apenas para executar a busca.
  if (props.mobile) {
    buscar();
    return;
  }

  // Comportamento original do desktop
  if (!buscaAberta.value) {
    buscaAberta.value = true;

    await nextTick();

    searchInputRef.value?.focus();
  } else {
    buscar();
  }
}

function handleClickOutside(event) {
  /*
   * No menu mobile não queremos
   * que o clique fora do input feche
   * a busca de forma inesperada.
   */
  if (searchRef.value && !searchRef.value.contains(event.target)) {
    buscaAberta.value = false;
  }
}

function buscar() {
  if (!termoBusca.value.trim()) return;

  router.push({
    path: "/resultados",
    query: {
      q: termoBusca.value.trim(),
    },
  });

  buscaAberta.value = false;
  termoBusca.value = "";

  if (props.mobile) {
    emit("fechar-menu");
  }
}

/* =========================
   EVENTOS
========================= */

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});

/* =========================
   ROTA ATUAL
========================= */

watch(
  () => route.path,
  (newPath) => {
    if (newPath === "/") {
      opcaoSelecionada.value = "";
    } else {
      const item = menuGerado.value.find((i) => {
        const path = i.route || i.page;

        if (!path) return false;

        return newPath === `/${path}` || newPath.startsWith(`/${path}/`);
      });

      opcaoSelecionada.value = item ? item.label : "";
    }
  },
  {
    immediate: true,
  }
);
</script>

<style scoped>
.main-menu {
  display: flex;
  justify-content: center;
  align-items: center;
}

.list-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.opcoes {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.menu-btn,
.admin {
  color: white;
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 6px 12px;
  font-size: 0.9rem;
  border-radius: 15px;
  white-space: nowrap;
  transition:
    background-color 0.2s ease,
    transform 0.2s ease;

  text-decoration: none;
}

.menu-btn:hover,
.admin:hover {
  background-color: #3b1f8c;
}

.opcao-selecionada {
  background-color: #3b1f8c;
}

.search-wrapper {
  position: relative;

  display: flex;

  align-items: center;
  justify-content: center;
}

.search-input {
  width: 0;
  padding: 0;
  border-radius: 15px;
  border: none;
  outline: none;
  opacity: 0;
  pointer-events: none;
  margin-left: 2rem;
  transition: all 0.2s ease;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-input.aberta {
  width: 250px;
  padding: 6px 35px 6px 10px;
  opacity: 1;
  pointer-events: auto;
  background: white;
  margin-left: 0;
  color: #333333;
}

.lupa {
  position: absolute;
  right: 10px;
  width: 20px;
  height: 20px;
  cursor: pointer;
  filter: brightness(0) invert(1);
  transition: all 0.3s ease;
}

.lupa.ativa {
  filter: none;
}

.main-menu-mobile {
  width: 100%;
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 20px 24px 40px;
  box-sizing: border-box;
}

.main-menu-mobile #menu {
  width: 100%;
}

.main-menu-mobile .list-group {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 8px;
}

.main-menu-mobile .opcoes {
  width: 100%;
  display: block;
}

.main-menu-mobile .menu-btn {
  width: 100%;
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
  padding: 12px 18px;
  border-radius: 14px;
  font-size: 1.05rem;
  font-weight: 500;
}

.main-menu-mobile .menu-btn:hover,
.main-menu-mobile .opcao-selecionada {
  background-color: #3b1f8c;
}

.main-menu-mobile .search-wrapper {
  width: 100%;
  min-height: 52px;
  margin-top: 12px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 14px;
}

.main-menu-mobile .search-input {
  width: 100%;
  height: 52px;
  margin: 0;
  padding: 6px 50px 6px 18px;
  opacity: 1;
  pointer-events: auto;
  background: transparent;
  color: white;
  font-size: 1rem;
}

.main-menu-mobile .search-input::placeholder {
  color: rgba(255, 255, 255, 0.75);
}

.main-menu-mobile .search-input:focus {
  background: white;

  color: #333;
}

.main-menu-mobile .search-input:focus::placeholder {
  color: #777;
}

.main-menu-mobile .lupa {
  right: 17px;

  width: 21px;
  height: 21px;
}
</style>
